import { mode, StyleFunctionProps } from '@chakra-ui/theme-tools'

import type { ThemeTypings } from '@chakra-ui/styled-system'

const baseStyle = {
  ':focus:not(:focus-visible)': {
    boxShadow: 'none',
  },
  fontWeight: 'medium',
  borderRadius: 'lg',
}

const sizes = {
  lg: {
    fontSize: 'md',
  },
  // A notch below Chakra's smallest, for buttons that sit inside a dense table cell.
  '2xs': {
    h: '1.25rem',
    minW: '1.25rem',
    fontSize: 'xs',
    px: 2,
  },
  xl: {
    h: '3.75rem',
    minW: '3.75rem',
    fontSize: 'lg',
    px: 7,
  },
}

// ThemeTypings includes an open `string` escape hatch; strip it so only known tokens remain.
type OpenChakraButtonSize = ThemeTypings['components']['Button']['sizes']
type KnownChakraButtonSize = OpenChakraButtonSize extends infer Size
  ? Size extends string
    ? string extends Size
      ? never
      : Size
    : never
  : never

/** Valid `size` values for Button in this app: Chakra defaults plus our custom sizes. */
export type AppButtonSize = KnownChakraButtonSize | keyof typeof sizes

const variants = {
  primary: (props: StyleFunctionProps) =>
    props.theme.components.Button.variants.solid({
      ...props,
      variant: 'solid',
      colorScheme: 'brand',
    }),
  'primary-on-accent': () => ({
    bg: 'brand.50',
    color: 'brand.600',
    _hover: { bg: 'brand.100' },
    _active: { bg: 'brand.100' },
  }),
  secondary: (props: StyleFunctionProps) =>
    props.theme.components.Button.variants.outline({
      ...props,
      variant: 'outline',
      colorScheme: 'gray',
    }),
  'secondary-on-accent': {
    color: 'white',
    borderColor: 'brand.50',
    borderWidth: '1px',
    _hover: { bg: 'whiteAlpha.200' },
    _active: { bg: 'whiteAlpha.200' },
  },
  ghost: (props: StyleFunctionProps) => ({
    color: 'emphasized',
    _hover: {
      bg: mode(
        'color-mix(in srgb, var(--chakra-colors-gray-100) 97%, black)',
        'color-mix(in srgb, var(--chakra-colors-gray-700) 92%, black)'
      )(props),
    },
    _active: {
      bg: mode(
        'color-mix(in srgb, var(--chakra-colors-gray-100) 97%, black)',
        'color-mix(in srgb, var(--chakra-colors-gray-700) 92%, black)'
      )(props),
    },
    _activeLink: {
      bg: mode('gray.100', 'gray.700')(props),
    },
  }),
  'ghost-on-accent': () => ({
    color: 'brand.50',
    _hover: {
      // 67% opacity using color-mix
      bg: 'color-mix(in srgb, var(--chakra-colors-brand-500) 67%, transparent)',
    },
    _activeLink: {
      color: 'white',
      bg: 'bg-accent-subtle',
    },
  }),
  link: (props: StyleFunctionProps) => {
    if (props.colorScheme === 'gray') {
      return {
        color: 'muted',
        _hover: {
          textDecoration: 'none',
          color: 'default',
        },
        _active: {
          color: 'default',
        },
      }
    }
    return {
      color: mode(`${props.colorScheme}.600`, `${props.colorScheme}.200`)(props),
      _hover: {
        color: mode(`${props.colorScheme}.700`, `${props.colorScheme}.300`)(props),
        textDecoration: 'none',
      },
      _active: {
        color: mode(`${props.colorScheme}.700`, `${props.colorScheme}.300`)(props),
      },
    }
  },
  'link-on-accent': () => {
    return {
      padding: 0,
      height: 'auto',
      lineHeight: 'normal',
      verticalAlign: 'baseline',
      color: 'brand.50',
      _hover: {
        color: 'white',
      },
      _active: {
        color: 'white',
      },
    }
  },
}

const defaultProps = {
  variant: 'secondary',
}

export default {
  baseStyle,
  variants,
  sizes,
  defaultProps,
}
